import React from 'react';
import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components/macro';
import { AnimatePresence, motion } from 'framer-motion';

import { useDataStore } from 'state/DataStore';

import Page from 'components/Page';
import InfoBox from 'components/InfoBox';
import InfoBoxHeader from 'components/InfoBox/InfoBoxHeader';
import InfoBoxArea from 'components/InfoBox/InfoBoxArea';
import ImagePopup from 'components/ImagePopup';
import ShortlistViewButton from 'components/ShortlistViewButton';
import ToggleShortlistButton from 'components/ToggleShortlistButton'; 
import AvailableUnitsBox from 'components/AvailableUnitsBox';

const Floorplan = observer(() => {
    const dataStore = useDataStore();
    const { levelSlug, unitSlug } = useParams();
    const { units } = dataStore.data;
    
    const level = dataStore.getItem('levels', 'slug', levelSlug);
    const unit = dataStore.getItem('units', 'slug', unitSlug);
    
    if (!(level && unit) || unit.acf.level !== level.id) {
        return (
            <Page>
                <h1>No Unit Found</h1>
            </Page>
        );
    }

    const unitType = dataStore.getItem('unit_types', 'id', unit.acf.unit_type);

    if (!unitType) {
        return (
            <Page>
                <h1>Unit has no unit type</h1>
            </Page>
        );
    }

    const availableUnits = units.filter(u => {
        return u.acf.unit_type === unitType.id && u.acf.status === 'available';
    });
    
    availableUnits.sort((a, b) => (
        a.title?.rendered?.localeCompare(b.title?.rendered)
    ));

    const areaData = [
        { name: 'Internal', value: `${unitType.acf.internal_area}m<sup>2</sup>` },
        { name: 'External', value: `${unitType.acf.external_area}m<sup>2</sup>` },
        { name: 'Total', value: `${unitType.acf.total_area}m<sup>2</sup>` },
    ];
    
    return (
        <Page>
            <AnimatePresence mode="wait">
                <FloorplanContent
                    key={unitSlug}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25 }}
                >
                    <Inner>
                        <CenterContent>
                            <ShortlistViewButton />

                            <PlanImage image={unitType.acf.floorplan} size="2048x2048" />

                            {availableUnits.length > 1 && (
                                <AvailableUnitsBox
                                    availableUnits={availableUnits}
                                    availableText={`Other apartments for Type ${unitType.title.rendered}`}
                                    currentUnit={unit}
                                />
                            )}

                            <KeyPlanImage src={unitType.acf.key_plan?.sizes?.main} alt="" />

                            <Disclaimer dangerouslySetInnerHTML={{ __html: dataStore.getSetting('apartment_settings', 'unit_disclaimer') }} />
                        </CenterContent>

                        <InfoBox>
                            <InfoBoxHeader unitType={unitType} unit={unit} level={level} />

                            <InfoBoxArea areaData={areaData} roomData={unitType.acf.room_areas} />

                            <InfoButtons>
                                <ShortlistButton unitId={unit.id} />
                            </InfoButtons>
                        </InfoBox>
                    </Inner>
                </FloorplanContent>
            </AnimatePresence>
        </Page>
    );
});


export default Floorplan;


const FloorplanContent = styled(motion.div)`
    position: absolute;
    width: 100%;
    height: 100%;
`;

const Inner = styled.div`
    display: flex;
    width: 100%;
    height: 100%;
`;

const CenterContent = styled.div`
    position: relative;
    flex: 1 1 auto;
    display: flex;
    padding: 9rem 10rem 20rem;
    // padding: 4rem 10rem 20rem;
    flex-direction: column;
    align-items: center;
    justify-content: center;
`;

const InfoButtons = styled.div`

`;

const PlanImage = styled(ImagePopup)`
    height: 100%;
    width: 100%;
    object-fit: contain;
`;

const KeyPlanImage = styled.img`
    position: absolute;
    bottom: 2rem;
    right: 5rem;
    height: 19rem;
    width: auto;
`;

const Disclaimer = styled.div`
    position: absolute;
    bottom: 3rem;
    left: 5rem;
    width: 90rem;
    font-size: 1.2rem;
    color: ${props => props.theme.primary_color};
    opacity: .4;

    p {
        margin: 0;
    }
`;

const ShortlistButton = styled(ToggleShortlistButton)`
    width: 100%;
`;
